import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  PLATFORM_ID,
  Inject,
  OnDestroy,
  AfterContentInit
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Breadcrumb, Product, ProductsService, Negotiation, User, Deal } from '@vestiaire/api-sdk-angular';
import { Shipping } from '@vestiaire/api-sdk-angular';
import { LocaleService } from '@app/core/i18n';
import { ContentRepositoryService } from '@app/core/services/content/content-repository.service';
import { select, Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import * as fromUser from '@app/core/user/store/reducers';
import * as fromDataLayer from '@app/analytics/actions/data-layer';
import { UserService } from '@app/core/user/services/user/user.service';
import * as mmaoActions from '@app/store/actions/mmao.action';
import { MmaoService } from '@app/shared/components/mmao/services/mmao.service';
import { UserTransactionRole } from '@app/core/user';
import { MatDialog } from '@angular/material/dialog';
import { SizeGuide } from '@app/shared/model/interfaces/sizeGuide/sizeGuide';
import { ConditionGuideModalComponent } from '@app/shared/components/condition-guide-modal/condition-guide-modal.component';
import { DiscussionsComponent } from '@app/shared/components/discussions/discussions.component';
import { LoadProductSuccess } from '@app/+product/store';
import * as reservationActions from './store/actions/reservation.action';
import { Subject } from 'rxjs';
import * as HeaderActions from '@app/core/header/store/actions';
import { PageService } from '@app/core/content/services';
import { ProductMicrodatasService } from '@app/+product/service/product-microdata.service';
import { Logger } from '@app/core/logging';
import { takeUntil } from 'rxjs/operators';
import { getDirectShippingEligibility } from '@app/core/i18n/store/reducers';
import { CarouselProductClick } from '@app/shared/components/carroussel-products/carroussel-products.component';
import { TrackProductCrossSellClick } from '@app/+catalog/store';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { ImagesService } from '@app/shared/components/images/services/images.service';

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit, AfterContentInit, OnDestroy {
  public productId: string;
  public product: Product;
  public shipping: Shipping;
  public similarProducts: Array<Product>;
  public toWearWithProducts: Array<Product>;
  public seenProducts: Array<Product> = [];
  public currency: string;
  public idSite: string;
  public countryCode: string;
  public regionCode: string;

  likedItem: string[] = [];

  public user: User | null;
  public userRole: UserTransactionRole = 'buyer';
  public negotiation: Negotiation | null;
  public negotiationsList: Negotiation[] | null;

  public breadcrumbList: Array<Breadcrumb> = [];

  public schemas = {};

  public sizeGuide: SizeGuide = {
    title: '',
    guide: []
  };

  public likeDisplay = {
    showCount: false,
    showIcon: true,
    inline: true,
    textAndCount: true,
    clickable: true,
    className: 'productPage__like'
  };

  public discussionsNb = 6;
  public showDiscussion = false;

  get userId(): string {
    return this.user ? this.user.id : null;
  }

  public shippingVoucherPath: string;
  public isServer = false;
  public directShippingEligible: boolean;
  public currentDeal: Deal;
  public isDealActive = false;
  private ngUnsubscribe = new Subject<void>();

  @ViewChild('block_comments', { static: false })
  block_comments: ElementRef;
  @ViewChild('block_wearwith', { static: false })
  block_wearwith: ElementRef;
  @ViewChild('block_discover', { static: false })
  block_discover: ElementRef;
  @ViewChild('block_description', { static: false })
  block_description: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private productService: ProductsService,
    private contentRepositoryService: ContentRepositoryService,
    private localeService: LocaleService,
    public helperService: HelpersService,
    private store$: Store<IAppState>,
    private userService: UserService,
    private mmaoService: MmaoService,
    private conditionModal: MatDialog,
    private router: Router,
    private pageService: PageService,
    private productMicrodatasService: ProductMicrodatasService,
    private logger: Logger,
    @Inject(PLATFORM_ID) private platformId: Object,
    private transferState: TransferState
  ) {
    this.regionCode = this.localeService.getRegionCode();
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.currency = this.localeService.getCurrency();
    this.idSite = this.localeService.getRegion().toString();
    this.isServer = isPlatformServer(this.platformId);
    this.productId = this.route?.snapshot?.params.id;
    const key = makeStateKey('product' + this.productId);
    if (this.transferState.hasKey(key)) {
      const datas = this.transferState.get(key, null);
      this.initProductDatas(datas);
    } else {
      this.route.data.subscribe(
        data => {
          this.transferState.set(key, data);
          if (data) {
            this.initProductDatas(data);
          }
        },
        err => {
          this.logger.error('No datas From resolver', err);
        }
      );
    }
    this.store$
      .select(getDirectShippingEligibility)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({ next: res => (this.directShippingEligible = this.isDirectShippingEligible(res)) });

    this.store$.pipe(select('catalog')).subscribe(
      res => {
        this.currentDeal = res?.deal || null;
        this.isDealActive = this.currentDeal?.active && this.product?.flags.includes('deal-eligible');
      },
      error => {
        console.error(error);
      }
    );
  }

  get isLiked(): boolean {
    if (!Array.isArray(this.likedItem)) {
      return false;
    }
    return this.likedItem.includes(String(this.productId));
  }

  ngOnInit() {
    this.setCountryCode();
    this.productId = this.route.params['_value'].id;

    if (!this.isServer) {
      this.userService.getAuthenticatedUser().subscribe(
        user => this.authenticate(user),
        err => this.authenticate()
      );
    }

    this.store$.pipe(select(fromUser.getLikedItems), takeUntil(this.ngUnsubscribe)).subscribe(
      productsLiked => {
        if (!productsLiked) {
          return;
        }
        this.likedItem = productsLiked['products'];
      },
      error => {
        this.logger.error('No datas For liked Product', error);
      }
    );
  }

  authenticate(user?: User): void {
    // When user is `undefined`
    // @todo check the reducer to understand why...
    this.user = user || null;
    if (user) {
      this.userRole = this.userService.getUserNegotiationRole(user, this.product);
      this.checkReservation();
      this.getRecentlySeenProducts();

      if (this.product.negotiable) {
        if (this.userRole === 'buyer') {
          this.getActiveBuyerNegotiation();
        } else {
          this.getActiveNegotiations();
        }
      }
    } else {
      this.userRole = 'buyer';
      this.negotiation = null;
    }
  }

  checkReservation(): void {
    this.store$.dispatch(new reservationActions.CheckReservation({ productId: this.product.id }));
  }

  ngAfterContentInit() {
    this.store$
      .select('mmao')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        res => {
          if (res && res.negotiation && res.negotiation.id && res.negotiation.product.id === this.product.id) {
            this.negotiation = res.negotiation;
            this.store$.dispatch({
              type: mmaoActions.REMOVE_MMAO,
              payload: { negotiation: {}, commentId: null }
            });
          }
        },
        err => console.log(err)
      );
  }

  private getActiveBuyerNegotiation() {
    this.mmaoService.getActiveBuyerNegotiation(this.productId, true).subscribe(
      negotiation => {
        this.negotiation = negotiation;
      },
      err => {
        this.negotiation = null;
      }
    );
  }

  private getActiveNegotiations() {
    this.mmaoService.getNegotiationsByProduct(this.productId).subscribe(
      (negotiations: Negotiation[]) => (this.negotiationsList = negotiations),
      err => (this.negotiationsList = null)
    );
  }

  transformDate(dateString) {
    return new Date(dateString);
  }

  getProductExtraInfo() {
    this.getSizeGuide();
    // shipping
    if (this.shipping) {
      // check if product can be in recently seen product
      if (
        this.shipping.buyable &&
        !this.shipping.prohibited &&
        this.product &&
        this.product.seller &&
        this.product.seller.id !== this.userId &&
        !this.product.sold
      ) {
        this.userService.addRecentlySeenProduct(this.product);
      }
    }

    // If product sold, get shipping voucher only for seller
    if (
      this.product &&
      this.product.seller &&
      this.product.seller.id === this.userId &&
      this.product.sold &&
      !this.product.receipt
    ) {
      this.getShippingVoucher();
    }

    // this.getAssociatedProduct(); TODO when api works
  }

  getAssociatedProduct() {
    this.productService.listAssociatedProducts(this.productId).subscribe(
      res => {
        if (res) {
          this.toWearWithProducts = res.data;
        }
      },
      error => {
        this.logger.error('No datas For listAssociatedProducts', error);
      }
    );
  }

  setCountryCode() {
    this.countryCode = this.localeService.getCountry();

    if (this.user && this.user.address && this.user.address.country) {
      this.countryCode = this.user.address.country.isoCode;
    }
  }

  getShippingProduct() {
    return this.productService.getProductShippingInformation(this.productId, this.countryCode);
  }

  getSizeGuide() {
    this.contentRepositoryService.getSizeGuide().subscribe(
      res => {
        const sizeGuideJson = res;
        if (this.product && this.product.universe && sizeGuideJson[this.product.universe.id]) {
          sizeGuideJson[this.product.universe.id]['categories'].map((category: any) => {
            if (category['content']['idCategory'].indexOf(parseInt(this.product.category.id, 10)) >= 0) {
              this.sizeGuide.guide = category['content']['guide'];
              this.sizeGuide.title = category['content']['title'];
            }
          });
        }
      },
      (err: HttpErrorResponse) => {
        this.logger.error('No datas For getSizeGuide', err);
      }
    );
  }

  getRecentlySeenProducts() {
    if (this.userId) {
      this.userService
        .getRecentlySeenProducts()
        .then((products: Array<Product>) => {
          this.seenProducts = this.mapProductPicturePath(products).filter(product => product.id !== this.product.id);
        })
        .catch(err => {
          this.logger.error('No datas For getRecentlySeenProducts', err);
        });
    }
  }

  mapProductPicturePath(data: Array<Product>): Array<Product> {
    return data.map(product => {
      // Do not modify data: it's the same value that gets transfered in server state
      // This is a workaround
      if (product.pictures[0].path.startsWith('/produit/')) {
        return product;
      }
      product.pictures[0].path = '/produit/' + product.pictures[0].path;
      return product;
    });
  }

  scrollTo(elementName: string) {
    if (this[elementName]) {
      const elementTopPosition =
        this[elementName].nativeElement.getBoundingClientRect().top + document.documentElement.scrollTop;
      const headerHeight = document.getElementsByClassName('mainHeader')[0].getBoundingClientRect().height;
      const scrollTo = this.helperService.device.isMdUp() ? elementTopPosition - headerHeight : elementTopPosition;
      this.helperService.smoothScrollTo(0, scrollTo);
    }
  }

  openConditionGuideModal() {
    this.productService.getProductConditionDetails(this.product.id).subscribe(
      data => {
        this.conditionModal.open(ConditionGuideModalComponent, {
          maxWidth: 600,
          data: data
        });
      },
      err => {
        this.logger.error('No datas For getProductConditionDetails', err);
      }
    );
  }

  getShippingVoucher() {
    this.productService.getShippingVoucher(this.productId).subscribe(
      res => {
        this.shippingVoucherPath = res.data.path;
      },
      err => {
        this.logger.error('No datas For getShippingVoucher', err);
      }
    );
  }

  private updatePageData(metadata): void {
    if (!metadata) {
      return;
    }
    this.pageService.setTitle(String(metadata.title));
    if (metadata.description) {
      this.pageService.setDescription(String(metadata.description));
    }
    if (metadata.keywords) {
      this.pageService.setKeywords(String(metadata.keywords));
    }
    if (this.product.indexation) {
      this.pageService.setRobots(this.product.indexation);
    }
    this.initAlternateLinks();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private initAlternateLinks(): void {
    if (this.product.alternateVersions) {
      this.pageService.clearLanguageVersions();
      this.pageService.setLanguageVersionsWithContentsLocalizedPath(this.product.alternateVersions);
    }
  }

  private isDirectShippingEligible(directShippingUser: boolean): boolean {
    return this.product && this.product.flags && directShippingUser && this.product.flags.includes('direct-shipping');
  }

  trackProductCrossSellClick(event: CarouselProductClick) {
    this.store$.dispatch(
      new TrackProductCrossSellClick({
        productPosition: event.position,
        productId: event.product.id
      })
    );
  }

  initProductDatas(data) {
    this.product = data.productPageData[0];
    this.similarProducts = data.productPageData[1] ? this.mapProductPicturePath(data.productPageData[1]) : [];
    this.breadcrumbList = data.productPageData[2];
    this.shipping = data.productPageData[3];
    if (this.product && this.product.metadata) {
      this.updatePageData(this.product.metadata);
    }
    if (this.product) {
      this.schemas = this.productMicrodatasService.getDatas(this.product);
      if (data.pageAnalytics) {
        this.store$.dispatch(new fromDataLayer.Persist(data.pageAnalytics));
        this.store$.dispatch(new LoadProductSuccess(this.product));
        const pageData = {
          pageType: 'product',
          idBrand: this.product.brand.id,
          idUniverse: this.product.universe.id,
          idCategory: this.product.category.parent ? this.product.category.parent.id : this.product.category.id
        };
        this.store$.dispatch(new HeaderActions.SetPageParams(pageData));
      }
      this.getProductExtraInfo();
    }
  }
}
